import type { ServiceType, SlotMode } from "../DTOs/SundayService";
import { inferServiceType } from "./serviceType";

// A series of services over a period (GOD-47): which dates, which of them
// exist already, and the slot modes a new service of a type starts with.
// Pure – see serviceSeries.test.ts.

export const WEEKDAY_NAMES_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

/** At most this many services in one go; at most this many days in the period. */
export const MAX_SERIES_ENTRIES = 200;
export const MAX_SERIES_DAYS = 366;

/** "auto" picks the type from the weekday, like the service form does. */
export type SeriesType = ServiceType | "auto";

export interface SeriesEntry {
    date: string; // YYYY-MM-DD
    weekday: string;
    serviceType: ServiceType;
    exists: boolean;
}

const DAY_MS = 86_400_000;
const toUtc = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
};
const isoOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** The legacy "manual" type counts as Sunday morning, as everywhere in the UI. */
function typeKey(type: ServiceType): ServiceType {
    return type === "manual" ? "sunday-morning" : type;
}

/** Key for "a service of this type on this day" – the same for new and existing services. */
export function serviceKey(date: string, type: ServiceType): string {
    return `${date}|${typeKey(type)}`;
}

/** Keys of existing services; their dates are stored as UTC midnight. */
export function existingServiceKeys(services: { date: Date; serviceType: ServiceType }[]): Set<string> {
    return new Set(services.map((s) => serviceKey(s.date.toISOString().slice(0, 10), s.serviceType)));
}

export type SeriesPlan = { entries: SeriesEntry[]; error?: undefined } | { entries: []; error: "range" | "too-long" | "too-many" | "no-weekday" };

/**
 * Every date from `from` to `to` (inclusive) whose weekday (0 = Sunday) is
 * selected, with the service type and whether such a service exists already.
 */
export function planSeries(from: string, to: string, weekdays: number[], type: SeriesType, existing: Set<string>): SeriesPlan {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to) || toUtc(to) < toUtc(from)) {
        return { entries: [], error: "range" };
    }
    if ((toUtc(to) - toUtc(from)) / DAY_MS + 1 > MAX_SERIES_DAYS) return { entries: [], error: "too-long" };
    if (weekdays.length === 0) return { entries: [], error: "no-weekday" };

    const entries: SeriesEntry[] = [];
    for (let ms = toUtc(from); ms <= toUtc(to); ms += DAY_MS) {
        const day = new Date(ms).getUTCDay();
        if (!weekdays.includes(day)) continue;
        const date = isoOf(ms);
        // inferServiceType reads the local weekday; noon avoids any date shift.
        const serviceType = type === "auto" ? inferServiceType(new Date(`${date}T12:00:00`)) : type;
        entries.push({ date, weekday: WEEKDAY_NAMES_DE[day], serviceType, exists: existing.has(serviceKey(date, serviceType)) });
    }
    if (entries.filter((e) => !e.exists).length > MAX_SERIES_ENTRIES) return { entries: [], error: "too-many" };
    return { entries };
}

/** Wochenschluss opens with a prayer fellowship (slot 1 "intro"); everything else starts with sermons. */
export function seriesSlotModes(type: ServiceType): { sermon1Mode: SlotMode; sermon2Mode: SlotMode } {
    return { sermon1Mode: type === "saturday" ? "intro" : "sermon", sermon2Mode: "sermon" };
}
